const buildLinkMap = (links = []) => {
  const map = {};
  links.forEach(item => {
    if (item?.source && item?.url) {
      map[item.source?.toLowerCase()] = item.url;
    }
  });
  return map;
};

const PRESAVE_STORE_MAP = {
  spotify: "spotify",
  amazon: "amazon",
  tidal: "tidal",
  deezer: "deezer",

  // special case
  youtube: "youtubemusic",
};

const buildPreSaveFollowPayload = (artistInfo) => {
  if (!artistInfo?.links || !Array.isArray(artistInfo.links)) {
    return [];
  }

  const seenStores = new Set();

  return artistInfo.links.reduce((acc, link) => {
    const mappedStoreId = PRESAVE_STORE_MAP[link.source];

    // skip unsupported stores
    if (!mappedStoreId) return acc;

    // avoid duplicates (e.g. multiple youtube / spotify)
    if (seenStores.has(mappedStoreId)) return acc;

    seenStores.add(mappedStoreId);

    acc.push({
      storeId: mappedStoreId,
      entities: [
        {
          url: link.url,
        },
      ],
    });

    return acc;
  }, []);
};
const getUserTimezone = () => {
  let timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Normalize deprecated timezone
  if (timezone === "Asia/Calcutta") {
    timezone = "Asia/Kolkata";
  }

  return timezone;
};

const validateScanLinkFrontend = (scanLink, scanLinkType) => {
  if (!scanLink) {
    return { valid: false, message: "Scan link is required" };
  }

  switch (scanLinkType) {
    case "url":
      try {
        const url = new URL(scanLink);
        if (!["http:", "https:"].includes(url.protocol)) {
          return { valid: false, message: "URL must start with http/https" };
        }
        return { valid: true };
      } catch {
        return { valid: false, message: "Invalid URL format" };
      }

    case "isrc":
      // RAW ISRC (no prefix)
      const cleaned = scanLink
        .replace(/^ISRC:/i, "")
        .replace(/-/g, "")
        .toUpperCase();
      if (!/^[A-Z]{2}[A-Z0-9]{3}\d{7}$/i.test(cleaned)) {
        return {
          valid: false,
          message: "ISRC must look like USRC17607839"
        };
      }
      return { valid: true };

    case "upc":
      // RAW UPC (digits only)
      if (!/^\d{12,14}$/.test(scanLink)) {
        return {
          valid: false,
          message: "UPC must be 12–14 digits"
        };
      }
      return { valid: true };

    default:
      return { valid: false, message: "Invalid scan link type" };
  }
};

const normalizeScanLinkForUI = (scanLink = "") => {
  if (!scanLink) return "";

  if (/^UPC:/i.test(scanLink)) {
    return scanLink.replace(/^UPC:/i, "");
  }

  if (/^ISRC:/i.test(scanLink)) {
    return scanLink.replace(/^ISRC:/i, "");
  }

  return scanLink; // URL stays as-is
};

// use for analytics chart
const DAY = 24 * 60 * 60 * 1000;

const parseDate = (d) => new Date(`${d}T00:00:00`);
const formatTimelineDate = (d) =>
  d.toLocaleDateString("en", { month: "short", day: "2-digit" });

const groupBy = (arr, keyFn) => {
  const map = {};
  arr.forEach(item => {
    const key = keyFn(item);
    if (!map[key]) {
      map[key] = { clicks: 0, clicksToService: 0 };
    }
    map[key].clicks += item.clicks || 0;
    map[key].clicksToService += item.clicksToService || 0;
  });
  return map;
};

const normalizeTimelineForChart = (
  timeline = [],
  range = "all-time"
) => {
  if (!timeline.length) {
    return { labels: [], clicks: [], streams: [] };
  }

  const dates = timeline.map(t => parseDate(t.date));
  const min = Math.min(...dates);
  const max = Math.max(...dates);
  const diffDays = Math.ceil((max - min) / DAY);

  // Decide effective range for "all-time" and yearly
  let effectiveRange = range;
  if (range === "all-time" || range === "yearly") {
    if (diffDays <= 7) effectiveRange = "weekly";
    else if (diffDays <= 45) effectiveRange = "monthly";
    else effectiveRange = "yearly";
  }

  // ---------------- WEEKLY ----------------
  if (effectiveRange === "weekly") {
    return {
      labels: timeline.map(t => t.date),
      clicks: timeline.map(t => t.clicks || 0),
      streams: timeline.map(t => t.clicksToService || 0)
    };
  }

  // ---------------- MONTHLY ----------------
  if (effectiveRange === "monthly") {
    if (diffDays <= 15) {
      return {
        labels: timeline.map(t => t.date),
        clicks: timeline.map(t => t.clicks || 0),
        streams: timeline.map(t => t.clicksToService || 0)
      };
    }
    // 4-day buckets
    const buckets = {};

    timeline.forEach(t => {
      const d = parseDate(t.date);
      const bucket = Math.floor((d - min) / (4 * DAY));
      if (!buckets[bucket]) {
        buckets[bucket] = {
          start: d,
          end: d,
          clicks: 0,
          streams: 0
        };
      }

      buckets[bucket].end = d;
      buckets[bucket].clicks += t.clicks || 0;
      buckets[bucket].streams += t.clicksToService || 0;
    });

    return {
      labels: Object.values(buckets).map(
        b => `${formatTimelineDate(b.start)}–${formatTimelineDate(b.end)}`
      ),
      clicks: Object.values(buckets).map(b => b.clicks),
      streams: Object.values(buckets).map(b => b.streams)
    };
  }

  // YEARLY 
  const grouped = groupBy(timeline, (t) => {
    const d = parseDate(t.date);
    return `${d.toLocaleString("en", { month: "short" })} ${d.getFullYear()}`;
  });

  return {
    labels: Object.keys(grouped),
    clicks: Object.values(grouped).map(v => v.clicks),
    streams: Object.values(grouped).map(v => v.clicksToService)
  };
};

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
const getCountryName = (code) => {
  try {
    if (!code) return "—";
    return regionNames.of(code.toUpperCase()) || code;
  } catch {
    return code;
  }
};

// for collection/fanbase and email data
const mapAudienceToRows = (audience = []) => {
  return audience.map((item, index) => {
    const user = item.userData || item || {};

    return {
      id: index,
      name: user.displayName || "Anonymous",
      email: user.email || user.webUrl || "—",
      webUrl: user.webUrl || "—",
      service: item.service === "widget_email" ? "Email" : item.service,
      country: getCountryName(user?.country) || "—",
      image: user.image || null,
      birthdate: user.birthdate || "—",
      profileFollowers: user.profileFollowers || 0,
      date: item.date
    };
  });
};



module.exports = {
  buildLinkMap, buildPreSaveFollowPayload, getUserTimezone, validateScanLinkFrontend, normalizeScanLinkForUI,
  normalizeTimelineForChart, mapAudienceToRows, getCountryName
}